import { GetApplicationResponse } from '@iwoca/lapi-client/edge';

import { TMobileSignupForm } from './form';
import { getUpdatedApplicationBody } from '../../../helpers/getApplicationBody';
import {
  TGetStateResponseDE,
  TPutStateRequestBodyDE,
} from '../../../lapiOverrideTypesDE';
import { TSupportedLocales } from '../../../types';
import { TCompanyDetailsDataDE } from '../../CompanyDetails/CompanyDetailsDE/CompanyDetailsDE';
import {
  denormaliseCompanyDataDE,
  denormalisePersonalDataDE,
  normaliseStepDataDE,
} from '../../helpers/stateNormalisation';
import {
  APPLICANT_STEP_ID,
  BUSINESS_STEP_ID,
  GET_STARTED_STEP_ID,
} from '../../helpers/steps';
import { TPersonalDetailsDataDE } from '../../PersonalDetails/PersonalDetailsDE/PersonalDetailsDE';

// --- RETRIEVE — backend objects → flat form ---

export function stateToForm(
  state: TGetStateResponseDE['data'],
): Partial<TMobileSignupForm> {
  const normalised = normaliseStepDataDE({
    data: state,
  } as TGetStateResponseDE);
  const getStarted = normalised[GET_STARTED_STEP_ID];
  const business = normalised[BUSINESS_STEP_ID];
  const applicant = normalised[APPLICANT_STEP_ID];

  return {
    email: getStarted?.email,
    companyName: business?.companyName,
    crefoId: business?.crefoId,
    legalForm: business?.legalForm,
    buildingNumber: business?.buildingNumber,
    street: business?.street,
    postcode: business?.postcode,
    town: business?.town,
    startDateMonth: business?.startDateMonth,
    startDateYear: business?.startDateYear,
    last12MonthsTurnover: business?.last12MonthsTurnover,
    title: applicant?.title,
    firstName: applicant?.firstName,
    lastName: applicant?.lastName,
    dobDay: applicant?.dobDay,
    dobMonth: applicant?.dobMonth,
    dobYear: applicant?.dobYear,
    phoneNumber: applicant?.phoneNumber,
    privacyPolicyAgreement: applicant?.privacyPolicyAgreement,
    homeStreet: applicant?.streetLine1,
    homeHouseNumber: applicant?.houseNumber,
    homePostcode: applicant?.postcode,
    homeTown: applicant?.town,
  };
}

export function applicationToForm(
  application?: GetApplicationResponse['data'],
): Partial<TMobileSignupForm> {
  const product = application?.requested_products?.[0];

  return {
    amount: product?.amount?.toString(),
    loanDuration:
      product?.qualitative_duration as TMobileSignupForm['loanDuration'],
    loanPurpose: product?.purpose as TMobileSignupForm['loanPurpose'],
    loanPurposeDetails: product?.detailed_purpose ?? undefined,
  };
}

// --- UPDATE — flat form → PUT body. uid minting happens inside the reused
// denormalisers, exactly as it does for the legacy desktop flow. ---

export function getStartedFormToApplicationPutBody(
  application: GetApplicationResponse['data'] | undefined,
  form: TMobileSignupForm,
  locale: TSupportedLocales,
) {
  return getUpdatedApplicationBody(
    application as GetApplicationResponse['data'],
    {
      amount: form.amount,
      loanDuration: form.loanDuration || undefined,
      loanPurpose: form.loanPurpose || undefined,
      loanPurposeDetails: form.loanPurposeDetails,
    },
    locale,
  );
}

export function companyFormToStatePutBody(
  prevState: TGetStateResponseDE['data'],
  form: TMobileSignupForm,
): TPutStateRequestBodyDE {
  const companyData: TCompanyDetailsDataDE = {
    companyName: form.companyName,
    crefoId: form.crefoId,
    legalForm: form.legalForm,
    buildingNumber: form.buildingNumber,
    street: form.street,
    postcode: form.postcode,
    town: form.town,
    startDateMonth: form.startDateMonth,
    startDateYear: form.startDateYear,
    last12MonthsTurnover: form.last12MonthsTurnover,
  };

  return denormaliseCompanyDataDE(prevState, companyData);
}

export function personalFormToStatePutBody(
  prevState: TGetStateResponseDE['data'],
  form: TMobileSignupForm,
): TPutStateRequestBodyDE {
  if (!form.title) {
    // Validation (added later, as in the legacy PersonalDetailsDE flow) guarantees a
    // title before this runs; fail loudly rather than invent a value if it doesn't.
    throw new Error('personalFormToStatePutBody: title is required');
  }

  const personalData: TPersonalDetailsDataDE = {
    title: form.title,
    firstName: form.firstName,
    lastName: form.lastName,
    dobDay: form.dobDay,
    dobMonth: form.dobMonth,
    dobYear: form.dobYear,
    phoneNumber: form.phoneNumber,
    privacyPolicyAgreement: form.privacyPolicyAgreement,
    streetLine1: form.homeStreet,
    houseNumber: form.homeHouseNumber,
    postcode: form.homePostcode,
    town: form.homeTown,
  };

  return denormalisePersonalDataDE(prevState, personalData);
}
